import {
  Component,
  computed,
  EventEmitter,
  inject,
  Input,
  Output,
  signal,
} from '@angular/core';
import { NgForOf, NgIf } from '@angular/common';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ChipModule } from 'primeng/chip';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { SelectButtonModule } from 'primeng/selectbutton';
import { FormsModule } from '@angular/forms';
import { DuplicateIndicator } from '../../../services/indicator/indicator-validator.service';
import { IndicatorStore } from '../../../stores/indicator/indicator.state';
import { IndicatorDataStore } from '../../../stores/indicatpr-data/indicator-data.state';
import { Indicator } from '../../../models/indicator';
import { FullIndicatorNameComponent } from '../../atoms/full-indicator-name/full-indicator-name.component';
import { BookmarkStore } from '../../../stores/bookmark/bookmark.state';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';

type FilterState = 'all' | 'withData' | 'withoutData';

@Component({
  selector: 'app-indicator-validation-list',
  standalone: true,
  imports: [
    NgForOf,
    NgIf,
    TableModule,
    TagModule,
    ChipModule,
    ButtonModule,
    CheckboxModule,
    SelectButtonModule,
    FormsModule,
    FullIndicatorNameComponent,
    TooltipModule,
  ],
  templateUrl: './indicator-validation-list.component.html',
  styleUrl: './indicator-validation-list.component.scss',
})
export class IndicatorValidationListComponent {
  @Input() duplicateIndicators: DuplicateIndicator[] = [];
  @Output() deleted = new EventEmitter();
  readonly indicatorStore = inject(IndicatorStore);
  readonly indicatorDataStore = inject(IndicatorDataStore);
  readonly bookmarkStore = inject(BookmarkStore);

  constructor(private confirmationDialogService: ConfirmationDialogService) {}
  // Filter state
  filterState = signal<FilterState>('all');

  filterOptions = [
    { label: 'Alle', value: 'all' },
    { label: 'mit Daten', value: 'withData' },
    { label: 'ohne Daten', value: 'withoutData' },
  ];

  // Safety checkbox for deletion
  canSafelyDelete = signal(false);
  forceDelete = signal(false);

  // Computed filtered list
  filteredIndicators = computed(() => {
    const state = this.filterState();
    const indicators = this.duplicateIndicators;

    if (state === 'all') {
      return indicators;
    } else if (state === 'withData') {
      return indicators.filter(item => item.dataCount > 0);
    } else {
      return indicators.filter(item => item.dataCount === 0);
    }
  });

  // Computed grouped list for display
  groupedIndicators = computed(() => {
    const filtered = this.filteredIndicators();
    const allIndicators = Object.values(this.indicatorStore.items());
    const groups: Array<{
      set?:
        | DuplicateIndicator
        | {
            indicator: Indicator;
            key: string;
            dataCount: number;
            duplicateCount: number;
          };
      children: DuplicateIndicator[];
      standalone?: DuplicateIndicator;
      isParentProxy?: boolean; // Flag to indicate this is a non-duplicate parent shown for grouping
    }> = [];

    // Track processed items
    const processed = new Set<string>();

    // First, identify all children and their parents
    const childrenWithParents = filtered.filter(item => item.indicator.parent);
    const parentIds = new Set(
      childrenWithParents.map(item => item.indicator.parent)
    );

    // Group children by parent
    const childrenByParent = new Map<string, DuplicateIndicator[]>();
    childrenWithParents.forEach(child => {
      const parentId = child.indicator.parent!;
      if (!childrenByParent.has(parentId)) {
        childrenByParent.set(parentId, []);
      }
      childrenByParent.get(parentId)?.push(child);
    });

    // Process each parent group
    parentIds.forEach(parentId => {
      const children = childrenByParent.get(parentId!) || [];

      // Find the parent indicator (either in filtered list or in all indicators)
      let parentItem = filtered.find(item => item.indicator._id === parentId);

      if (!parentItem) {
        // Parent is not duplicated, but we need to show it for grouping
        const parentIndicator = allIndicators.find(ind => ind._id === parentId);
        if (parentIndicator) {
          parentItem = {
            indicator: parentIndicator as Indicator & { _id: string },
            key: parentIndicator.key || '',
            dataCount: 0,
            duplicateCount: 1, // Not actually duplicated
          };
          groups.push({
            set: parentItem,
            children: children,
            isParentProxy: true, // Mark as non-duplicate parent
          });
        }
      } else {
        // Parent is also duplicated
        groups.push({
          set: parentItem,
          children: children,
          isParentProxy: false,
        });
        processed.add(parentItem.indicator._id);
      }

      // Mark children as processed
      children.forEach(child => processed.add(child.indicator._id));
    });

    // Now process remaining items (sets without children in filtered list, and standalone indicators)
    filtered.forEach(item => {
      if (processed.has(item.indicator._id)) return;

      if (item.indicator.isSet) {
        // Set with no duplicated children (but might have children)
        const children = allIndicators.filter(
          child => child.parent === item.indicator._id
        );

        processed.add(item.indicator._id);
        groups.push({
          set: item,
          children: [], // No duplicated children to show
          isParentProxy: false,
        });
      } else if (!item.indicator.parent) {
        // Standalone indicator (no parent, not a set)
        processed.add(item.indicator._id);
        groups.push({
          standalone: item,
          children: [],
        });
      }
    });

    return groups;
  });

  cols = [
    { field: 'isSet', header: '', tooltip: '' },
    { field: 'name', header: 'Name', tooltip: '' },
    {
      field: 'key',
      header: 'Schlüssel',
      icon: 'fas fa-key',
      tooltip: '',
    },
    {
      field: 'dataCount',
      header: '',
      icon: 'fas fa-database',
      tooltip: 'Anzahl der Daten',
    },
    {
      field: 'duplicateCount',
      header: '',
      icon: 'fas fa-exclamation-circle',
      tooltip: 'Anzahl der Duplikate',
    },
    {
      field: 'bookmarkReferences',
      header: '',
      icon: 'fas fa-note-sticky',
      tooltip: 'Anzahl der referenzierten Ansichten',
    },
  ];

  async deleteIndicatorsWithoutData() {
    const forceDeleteEnabled = this.forceDelete();

    // Group indicators by key
    const indicatorsByKey = new Map<string, DuplicateIndicator[]>();
    this.duplicateIndicators.forEach(item => {
      if (!indicatorsByKey.has(item.key)) {
        indicatorsByKey.set(item.key, []);
      }
      indicatorsByKey.get(item.key)?.push(item);
    });

    const indicatorsToDelete: Indicator[] = [];
    const allIndicators = Object.values(this.indicatorStore.items());

    // For each key group, decide which to delete
    indicatorsByKey.forEach((group, key) => {
      // Separate sets from regular indicators
      const sets = group.filter(item => item.indicator.isSet);
      const regularIndicators = group.filter(item => !item.indicator.isSet);

      // Handle regular indicators (non-sets)
      const topLevelIndicators = regularIndicators.filter(
        i => !i.indicator.parent
      );

      if (forceDeleteEnabled) {
        // Force delete: keep oldest, delete all others regardless of data
        if (topLevelIndicators.length > 1) {
          topLevelIndicators.sort((a, b) =>
            a.indicator._id.localeCompare(b.indicator._id)
          );
          topLevelIndicators.slice(1).forEach(i => {
            if (!indicatorsToDelete.includes(i.indicator as Indicator)) {
              indicatorsToDelete.push(i.indicator as Indicator);
            }
          });
        }
      } else {
        // Normal mode: only delete duplicates without data
        regularIndicators.forEach(item => {
          // If regular indicator has no parent (top-level) and no data: delete all except oldest
          if (!item.indicator.parent && item.dataCount === 0) {
            // Group by creation date, keep oldest
            const topLevelWithoutData = regularIndicators.filter(
              i => !i.indicator.parent && i.dataCount === 0
            );
            if (topLevelWithoutData.length > 1) {
              // Sort by _id (oldest first, assuming _id contains timestamp)
              topLevelWithoutData.sort((a, b) =>
                a.indicator._id.localeCompare(b.indicator._id)
              );
              // Delete all except the first (oldest)
              topLevelWithoutData.slice(1).forEach(i => {
                if (!indicatorsToDelete.includes(i.indicator as Indicator)) {
                  indicatorsToDelete.push(i.indicator as Indicator);
                }
              });
            }
          }
        });
      }

      // Handle sets (indicators with isSet = true)
      if (sets.length > 1) {
        // For each set, check if it has children with data
        sets.forEach(setItem => {
          const setId = setItem.indicator._id;

          // Find all children of this set
          const children = allIndicators.filter(ind => ind.parent === setId);

          // Check if any child has data
          const childrenWithData = children.filter(child => {
            const dataCount =
              this.indicatorDataStore.countByIndicator(child._id) || 0;
            return dataCount > 0;
          });

          // If no children have data, mark the entire set for deletion
          if (
            (forceDeleteEnabled || childrenWithData.length === 0) &&
            children.length > 0
          ) {
            // Delete the set
            if (!indicatorsToDelete.includes(setItem.indicator as Indicator)) {
              indicatorsToDelete.push(setItem.indicator as Indicator);
            }
            // Delete all its children
            children.forEach(child => {
              if (!indicatorsToDelete.includes(child as Indicator)) {
                indicatorsToDelete.push(child as Indicator);
              }
            });
          }
        });
      }

      // Handle children of sets (indicators with parent)
      const childIndicators = regularIndicators.filter(
        item => item.indicator.parent
      );
      if (childIndicators.length > 0) {
        // Group children by their parent
        const childrenByParent = new Map<string, DuplicateIndicator[]>();
        childIndicators.forEach(child => {
          const parentId = child.indicator.parent!;
          if (!childrenByParent.has(parentId)) {
            childrenByParent.set(parentId, []);
          }
          childrenByParent.get(parentId)?.push(child);
        });

        // For each parent group, delete children without data if parent set is being kept
        childrenByParent.forEach((siblings, parentId) => {
          const parentSet = allIndicators.find(ind => ind._id === parentId);

          // Only delete children without data if the parent set is not being deleted
          if (
            parentSet &&
            !indicatorsToDelete.includes(parentSet as Indicator)
          ) {
            if (forceDeleteEnabled) {
              // Force delete: keep oldest child, delete all others
              if (siblings.length > 1) {
                siblings.sort((a, b) =>
                  a.indicator._id.localeCompare(b.indicator._id)
                );
                siblings.slice(1).forEach(child => {
                  if (
                    !indicatorsToDelete.includes(child.indicator as Indicator)
                  ) {
                    indicatorsToDelete.push(child.indicator as Indicator);
                  }
                });
              }
            } else {
              // Normal mode: only delete children without data
              siblings.forEach(child => {
                if (
                  child.dataCount === 0 &&
                  !indicatorsToDelete.includes(child.indicator as Indicator)
                ) {
                  indicatorsToDelete.push(child.indicator as Indicator);
                }
              });
            }
          }
        });
      }
    });

    // Additional pass: Handle children with same parent but different keys
    // Group all duplicate indicators by parent
    const duplicatesByParent = new Map<string, DuplicateIndicator[]>();
    this.duplicateIndicators.forEach(item => {
      if (item.indicator.parent) {
        const parentId = item.indicator.parent;
        if (!duplicatesByParent.has(parentId)) {
          duplicatesByParent.set(parentId, []);
        }
        duplicatesByParent.get(parentId)?.push(item);
      }
    });

    // For each parent, check for duplicated children without data
    duplicatesByParent.forEach((children, parentId) => {
      // Group children by key within this parent
      const childrenByKey = new Map<string, DuplicateIndicator[]>();
      children.forEach(child => {
        if (!childrenByKey.has(child.key)) {
          childrenByKey.set(child.key, []);
        }
        childrenByKey.get(child.key)?.push(child);
      });

      // For each key group, delete duplicates without data
      childrenByKey.forEach((keyGroup, key) => {
        if (keyGroup.length > 1) {
          if (forceDeleteEnabled) {
            // Force delete: keep oldest, delete all others regardless of data
            keyGroup.sort((a, b) =>
              a.indicator._id.localeCompare(b.indicator._id)
            );
            keyGroup.slice(1).forEach(child => {
              if (!indicatorsToDelete.includes(child.indicator as Indicator)) {
                indicatorsToDelete.push(child.indicator as Indicator);
              }
            });
          } else {
            // Normal mode: Multiple children with same key under same parent
            const withData = keyGroup.filter(c => c.dataCount > 0);
            const withoutData = keyGroup.filter(c => c.dataCount === 0);

            if (withData.length > 0) {
              // Keep all with data, delete all without data
              withoutData.forEach(child => {
                if (
                  !indicatorsToDelete.includes(child.indicator as Indicator)
                ) {
                  indicatorsToDelete.push(child.indicator as Indicator);
                }
              });
            } else {
              // All without data - keep oldest, delete rest
              withoutData.sort((a, b) =>
                a.indicator._id.localeCompare(b.indicator._id)
              );
              withoutData.slice(1).forEach(child => {
                if (
                  !indicatorsToDelete.includes(child.indicator as Indicator)
                ) {
                  indicatorsToDelete.push(child.indicator as Indicator);
                }
              });
            }
          }
        }
      });
    });

    // Second pass: Check if any sets should be deleted because all their children are being deleted
    indicatorsByKey.forEach((group, key) => {
      const sets = group.filter(item => item.indicator.isSet);

      sets.forEach(setItem => {
        // Skip if already marked for deletion
        if (indicatorsToDelete.includes(setItem.indicator as Indicator)) {
          return;
        }

        const setId = setItem.indicator._id;
        // Find all children of this set
        const children = allIndicators.filter(ind => ind.parent === setId);

        if (children.length > 0) {
          // Check if ALL children are being deleted
          const allChildrenBeingDeleted = children.every(child =>
            indicatorsToDelete.includes(child as Indicator)
          );

          // If all children are being deleted, delete the set too
          if (allChildrenBeingDeleted) {
            indicatorsToDelete.push(setItem.indicator as Indicator);
          }
        }
      });
    });

    if (indicatorsToDelete.length === 0) {
      return;
    }

    try {
      await this.indicatorStore.removeMultiple({
        items: indicatorsToDelete,
        message: {
          severity: 'success',
          summary: 'Löschen abgeschlossen',
          detail: `${indicatorsToDelete.length} doppelte Indikatoren wurden erfolgreich gelöscht.`,
        },
      });
      // Reset checkboxes after successful deletion
      this.canSafelyDelete.set(false);
      this.forceDelete.set(false);
      setTimeout(() => {
        this.deleted.emit();
      }, 300);
    } catch (error) {
      console.error('Error deleting indicators:', error);
    }
  }

  /**
   * Handles the confirmation dialog for deleting an indicator.
   *
   * @param {Indicator} indicator - The indicator object to be deleted.
   * @return {void} This method does not return a value.
   */
  confirmDeletion(event: any, indicator: Indicator) {
    this.indicatorStore.singleSelection(indicator._id);
    setTimeout(() => {
      this.confirmationDialogService.confirmIndicatorDeletion(
        event,
        indicator,
        () => {
          if (indicator) {
            this.indicatorStore.deleteItem(indicator);
            this.indicatorStore.clearSelection();
          }
        }
      );
    }, 300);
  }
}
